import React, {
  useState,
  useEffect,
  useMemo,
  useImperativeHandle,
  forwardRef,
} from "react";
import { Row, Col, FormGroup, Label, Input } from "reactstrap";
import Select from "components/Select";
import ReactDatetime from "components/CustomDatePicker";
import { getActiveColorStyle } from "../../utils/colorUtils";
import { getSelectStyles } from "../../utils/selectStyles";
import { getTheme2SearchFilterSingleSelectStyles } from "utils/theme2MultiSelect";
import { formatDateForPicker } from "../../utils/dateUtils";
import { formatPhoneNumber } from "../../utils/phoneUtils";
import { useGetCountries } from "api/countries/service";
import { useGetChannels } from "api/channels/service";

const PortfolioDetail = forwardRef(
  ({ data, activeColor, isTheme2 = false, portfolioId, updateMutation }, ref) => {
    const activeColorStyle = useMemo(
      () => getActiveColorStyle(activeColor),
      [activeColor]
    );
    const [isLightTheme, setIsLightTheme] = useState(false);
    const { data: countriesDataOriginal } = useGetCountries();
    const countriesData = countriesDataOriginal?.data || [];
    const { data: bookkeepingCompany } = useGetChannels("bookkeeping_company");

    // Form state for editing
    const [formData, setFormData] = useState({});

    // Initialize formData when data changes
    useEffect(() => {
      if (data) {
        setFormData(data);
      }
    }, [data]);

    useEffect(() => {
      const checkTheme = () => {
        setIsLightTheme(document.body.classList.contains("white-content"));
      };
      checkTheme();

      const observer = new MutationObserver(checkTheme);
      observer.observe(document.body, {
        attributes: true,
        attributeFilter: ["class"],
      });

      return () => observer.disconnect();
    }, []);

    const selectStyles = useMemo(
      () =>
        isTheme2
          ? getTheme2SearchFilterSingleSelectStyles(activeColorStyle, true)
          : getSelectStyles(activeColorStyle, isLightTheme),
      [activeColorStyle, isLightTheme, isTheme2]
    );
    const inputStyle = isTheme2 ? { fontSize: "14px" } : undefined;
    const dateInputStyle = isTheme2 ? { fontSize: "14px" } : { borderRadius: 0 };

    const typeOptions = [
      { value: "Flow Pool", label: "Flow Pool" },
      { value: "Bulk Sale", label: "Bulk Sale" },
      { value: "Spec Pool", label: "Spec Pool" },
    ];

    const yesNoOptions = [
      { value: false, label: "No" },
      { value: true, label: "Yes" },
    ];

    const bookkeepingCompanyId =
      typeof formData?.bookkeeping_company === "object"
        ? formData?.bookkeeping_company?.id
        : formData?.bookkeeping_company;
    const countryId =
      typeof formData?.country === "object"
        ? formData?.country?.id
        : formData?.country;
    const stateId =
      typeof formData?.state === "object"
        ? formData?.state?.id
        : formData?.state;

    const selectedCountry = useMemo(
      () => countriesData?.find((c) => String(c.id) === String(countryId)),
      [countriesData, countryId]
    );
    const derivedStateOptions = selectedCountry?.states || [];
    const hasStates =
      Array.isArray(derivedStateOptions) && derivedStateOptions.length > 0;

    // Expose handleSave to parent via ref
    useImperativeHandle(ref, () => ({
      handleSave: async () => {
        try {
          // Prepare payload with form data
          const payload = {
            bookkeeping_company: formData?.bookkeeping_company,
            type: formData?.type,
            description: formData?.description,
            name: formData?.name,
            date: formData?.date,
            active: formData?.active,
            in_house: formData?.in_house,
            stock_namer_as_loan_number: formData?.stock_namer_as_loan_number,
            loan_number_prefix: formData?.loan_number_prefix,
            next_loan_number: formData?.next_loan_number,
            address_name: formData?.address_name,
            address_text_name: formData?.address_text_name,
            address_1: formData?.address_1,
            address_2: formData?.address_2,
            zip: formData?.zip,
            city: formData?.city,
            state: formData?.state,
            phone: formData?.phone,
            fax: formData?.fax,
            website: formData?.website,
            receipt_message: formData?.receipt_message,
          };

          await updateMutation.mutateAsync({
            id: portfolioId,
            data: payload,
          });

          return { success: true };
        } catch (error) {
          console.error("Error saving portfolio detail:", error);
          return { success: false, error };
        }
      },
    }));

    return (
      <Row>
        {/* Left Column - PORTFOLIO DETAIL */}
        <Col md="6">
          <div
            className="card"
            style={{ backgroundColor: activeColorStyle.backgroundColor }}
          >
            <div className="card-header font-weight-bold text-uppercase">
              PORTFOLIO DETAIL
            </div>
            <div className="card-body">
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label>What Bookkeeping Company Owns This Portfolio:</Label>
                    <Select
                      activeColor={activeColor}
                      styles={selectStyles}
                      className="react-select"
                      placeholder="Select Bookkeeping Company"
                      name="bookkeeping_company"
                      isSearchable
                      closeMenuOnSelect={true}
                      value={
                        bookkeepingCompanyId
                          ? {
                              value: bookkeepingCompanyId,
                              label:
                                bookkeepingCompany?.results?.find(
                                  (bc) => bc.id === bookkeepingCompanyId
                                )?.name || bookkeepingCompanyId,
                            }
                          : null
                      }
                      onChange={(value) =>
                        setFormData((prev) => ({
                          ...prev,
                          bookkeeping_company: value?.value || "",
                        }))
                      }
                      options={
                        bookkeepingCompany?.results?.map((bc) => ({
                          value: bc.id,
                          label: bc.name,
                        })) || []
                      }
                    />
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label>
                      Accounting Class:{" "}
                      <i className="tim-icons icon-alert-circle-exc text-info" />
                    </Label>
                    <Select
                      activeColor={activeColor}
                      styles={selectStyles}
                      className="react-select"
                      placeholder="Select Accounting Class"
                      name="accounting_class"
                      isSearchable
                      isDisabled
                      closeMenuOnSelect={true}
                      value={null}
                      options={[]}
                    />
                  </FormGroup>
                </Col>
              </Row>
              <FormGroup>
                <Label>Portfolio ID: </Label>
                <span> {" " + data?.code}</span>
              </FormGroup>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label>Portfolio Type:</Label>
                    <Select
                      activeColor={activeColor}
                      styles={selectStyles}
                      className="react-select"
                      placeholder="Select"
                      name="type"
                      isSearchable
                      closeMenuOnSelect={true}
                      value={
                        formData?.type
                          ? { value: formData.type, label: formData.type }
                          : null
                      }
                      onChange={(value) =>
                        setFormData((prev) => ({
                          ...prev,
                          type: value?.value || "",
                        }))
                      }
                      options={typeOptions}
                    />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>Portfolio Name:</Label>
                    <Input
                      type="text"
                      className="form-control"
                      style={inputStyle}
                      value={formData?.name || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          name: e.target.value,
                        }))
                      }
                    />
                  </FormGroup>
                </Col>
              </Row>

              <FormGroup>
                <Label>Portfolio Description:</Label>
                <Input
                  type="textarea"
                  rows="3"
                  className="form-control"
                  style={inputStyle}
                  value={formData?.description || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  placeholder="Write a description"
                />
              </FormGroup>

              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label>Origination Date:</Label>
                    <ReactDatetime
                      inputProps={{
                        className: "form-control",
                        placeholder: "Select Date",
                        style: dateInputStyle,
                      }}
                      value={
                        formData?.date ? formatDateForPicker(formData.date) : ""
                      }
                      onChange={(value) =>
                        setFormData((prev) => ({ ...prev, date: value }))
                      }
                      dateFormat="MM-DD-YYYY"
                      timeFormat={false}
                      closeOnSelect={true}
                    />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>Portfolio Actively Used:</Label>
                    <Select
                      activeColor={activeColor}
                      styles={selectStyles}
                      className="react-select"
                      placeholder="Select"
                      name="active"
                      isSearchable={false}
                      closeMenuOnSelect={true}
                      value={yesNoOptions.find(
                        (opt) => opt.value === formData?.active
                      )}
                      onChange={(value) =>
                        setFormData((prev) => ({
                          ...prev,
                          active: value?.value ?? false,
                        }))
                      }
                      options={yesNoOptions}
                    />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label>Portfolio Serviced "In House":</Label>
                    <Select
                      activeColor={activeColor}
                      styles={selectStyles}
                      className="react-select"
                      placeholder="Select"
                      name="in_house"
                      isSearchable={false}
                      closeMenuOnSelect={true}
                      value={yesNoOptions.find(
                        (opt) => opt.value === formData?.in_house
                      )}
                      onChange={(value) =>
                        setFormData((prev) => ({
                          ...prev,
                          in_house: value?.value ?? false,
                        }))
                      }
                      options={yesNoOptions}
                    />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>Use Stock number as Loan Number:</Label>
                    <Select
                      activeColor={activeColor}
                      styles={selectStyles}
                      className="react-select"
                      placeholder="Select"
                      name="stock_namer_as_loan_number"
                      isSearchable={false}
                      closeMenuOnSelect={true}
                      value={yesNoOptions.find(
                        (opt) =>
                          opt.value === formData?.stock_namer_as_loan_number
                      )}
                      onChange={(value) =>
                        setFormData((prev) => ({
                          ...prev,
                          stock_namer_as_loan_number: value?.value ?? false,
                        }))
                      }
                      options={yesNoOptions}
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label>Loan Number Prefix: (i.e., 001)</Label>
                    <Input
                      type="text"
                      className="form-control"
                      style={inputStyle}
                      value={formData?.loan_number_prefix || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          loan_number_prefix: e.target.value,
                        }))
                      }
                    />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>Next Loan Number: (i.e., 1000)</Label>
                    <Input
                      type="text"
                      className="form-control"
                      style={inputStyle}
                      value={formData?.next_loan_number || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          next_loan_number: e.target.value,
                        }))
                      }
                    />
                  </FormGroup>
                </Col>
              </Row>
            </div>
          </div>
        </Col>

        {/* Right Column - ADDRESS ON ACCT COLLECTION LETTERS */}
        <Col md="6">
          <div
            className="card"
            style={{ backgroundColor: activeColorStyle.backgroundColor }}
          >
            <div className="card-header font-weight-bold text-uppercase">
              ADDRESS USED ON ACCOUNT COLLECTION LETTERS
            </div>
            <div className="card-body">
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label>Name:</Label>
                    <Input
                      type="text"
                      className="form-control"
                      style={inputStyle}
                      value={formData?.address_name || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          address_name: e.target.value,
                        }))
                      }
                    />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>
                      Text Name: (what appears in texting message; maximum 12
                      characters)
                    </Label>
                    <Input
                      type="text"
                      className="form-control"
                      style={inputStyle}
                      maxLength={12}
                      value={formData?.address_text_name || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          address_text_name: e.target.value,
                        }))
                      }
                    />
                  </FormGroup>
                </Col>
              </Row>
              <FormGroup>
                <Label>Address 1:</Label>
                <Input
                  type="text"
                  className="form-control"
                  style={inputStyle}
                  value={formData?.address_1 || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      address_1: e.target.value,
                    }))
                  }
                />
              </FormGroup>

              <FormGroup>
                <Label>Address 2:</Label>
                <Input
                  type="text"
                  className="form-control"
                  style={inputStyle}
                  value={formData?.address_2 || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      address_2: e.target.value,
                    }))
                  }
                />
              </FormGroup>

              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label>Zip:</Label>
                    <Input
                      type="text"
                      className="form-control"
                      style={inputStyle}
                      value={formData?.zip || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          zip: e.target.value,
                        }))
                      }
                    />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>City:</Label>
                    <Input
                      type="text"
                      className="form-control"
                      style={inputStyle}
                      value={formData?.city || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          city: e.target.value,
                        }))
                      }
                      placeholder="Enter city"
                    />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>State:</Label>
                    <Select
                      activeColor={activeColor}
                      styles={selectStyles}
                      className="react-select"
                      placeholder={
                        !countryId
                          ? "Select country first"
                          : hasStates
                          ? "Select state"
                          : "No states for selected country"
                      }
                      name="state"
                      isSearchable
                      closeMenuOnSelect={true}
                      value={
                        stateId
                          ? {
                              value: stateId,
                              label: derivedStateOptions?.find(
                                (s) => String(s.id) === String(stateId)
                              )?.name,
                            }
                          : null
                      }
                      onChange={(value) =>
                        setFormData((prev) => ({
                          ...prev,
                          state: value?.value || "",
                        }))
                      }
                      options={
                        hasStates
                          ? derivedStateOptions.map((s) => ({
                              value: String(s.id),
                              label: s.name,
                            }))
                          : []
                      }
                      isDisabled={!hasStates}
                    />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>Phone:</Label>
                    <Input
                      type="text"
                      className="form-control"
                      style={inputStyle}
                      value={formData?.phone || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          phone: e.target.value,
                        }))
                      }
                    />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>Fax:</Label>
                    <Input
                      type="text"
                      className="form-control"
                      style={inputStyle}
                      value={formData?.fax || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          fax: e.target.value,
                        }))
                      }
                    />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>Website:</Label>
                    <Input
                      type="text"
                      className="form-control"
                      style={inputStyle}
                      value={formData?.website || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          website: e.target.value,
                        }))
                      }
                    />
                  </FormGroup>
                </Col>
              </Row>

              <FormGroup>
                <Label>Pmt Receipt Message: (150 Characters only)</Label>
                <Input
                  type="textarea"
                  rows="3"
                  className="form-control"
                  style={inputStyle}
                  maxLength={150}
                  value={formData?.receipt_message || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      receipt_message: e.target.value,
                    }))
                  }
                />
              </FormGroup>
            </div>
          </div>
        </Col>
      </Row>
    );
  }
);

PortfolioDetail.displayName = "PortfolioDetail";

export default PortfolioDetail;
